import { getInstanceId } from '@va/dashboard/selectors/app';
import { ApiOperators, DefaultFilterOptionType } from '@va/types/filters';
import { PaginatedResponse, SortingDirection } from '@va/types/requests';
import { AutocompleteOptionsFetcher } from '@va/ui/design-system';
import { isDefaultWebsiteId } from '@va/util/helpers';
import { useFetchData } from '@va/util/hooks';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { SessionDimensions, SessionMetrics, SessionMetricsResponse, useSessionMetrics } from './sessions.api';

export const useTrafficChannelValues = (channel: string) => {
  const websiteId = useSelector(getInstanceId);
  // TODO: Use PagintedResponse type once the pagination is implemented on BE
  const mapper = useCallback(({ payload }: { meta: { total: number }; payload: { values: string[] } }) => {
    return payload.values.filter((item) => item.trim() !== '').map((item) => ({ label: item, value: item }));
  }, []);

  return useFetchData<DefaultFilterOptionType[]>(
    isDefaultWebsiteId(websiteId) ? null : `/v2/websites/${websiteId}/traffic-api/values/${channel}`,
    { revalidateIfStale: false, revalidateOnFocus: false },
    mapper,
  );
};

const REFERRER_URL_DIMENSIONS = ['referrerUrl'] satisfies SessionDimensions[];
const REFERRER_URL_METRICS = ['sessionCount'] satisfies SessionMetrics[];

export const useAutocompleteReferrersSuggestions: AutocompleteOptionsFetcher = (
  search,
  shouldFetch,
  pagination,
  controller,
) => {
  const mapper = useCallback(
    ({
      payload,
      meta,
    }: PaginatedResponse<
      SessionMetricsResponse<(typeof REFERRER_URL_METRICS)[number], (typeof REFERRER_URL_DIMENSIONS)[number]>[]
    >) => {
      return {
        items: payload.map(({ referrerUrl }, index) => ({ value: referrerUrl, id: `${index}_${referrerUrl}` })),
        total: meta.total,
      };
    },
    [],
  );

  return useSessionMetrics(
    {
      trace: 'referrers-urls',
      metrics: REFERRER_URL_METRICS,
      dimensions: REFERRER_URL_DIMENSIONS,
      controller,
      pagination,
      order: [{ member: 'sessionCount', direction: SortingDirection.Desc }],
      where: {
        and: [
          {
            member: 'hasThirdPartyReferrer',
            operator: ApiOperators.eq,
            values: [true],
          },
          {
            member: 'referrerUrl',
            operator: ApiOperators.in,
            values: [search],
          },
        ],
      },
    },
    shouldFetch,
    mapper,
  );
};
